import { Chip, Avatar, Box } from '@mui/material';
import { chipColors, onLight } from '../../lib/contrast';
import { useTranslation } from 'react-i18next';

export type DeviceType =
    | 'PIN'
    | 'QR'
    | 'CARD'
    | 'TICKET_CARD'
    | 'FINGERPRINT'
    | 'TICKET_ONE_USE';

type TypeObj = {
    type: DeviceType;
};

type TypeConfig = {
    letter: string;
    color: string;
    textColor: string;
};

type Props = {
    types?: TypeObj[];
};

/**
 * Type configuration with colors (same as SttChipUnlocker)
 */
const typeConfig: Record<DeviceType, TypeConfig> = {
    PIN: { letter: 'P', color: '#4CAF50', textColor: 'white' }, // Green
    QR: { letter: 'Q', color: '#2196F3', textColor: 'white' }, // Blue
    CARD: { letter: 'C', color: '#FF9800', textColor: 'white' }, // Orange
    TICKET_CARD: { letter: 'T', color: '#9C27B0', textColor: 'white' }, // Purple
    FINGERPRINT: { letter: 'F', color: '#E91E63', textColor: 'white' }, // Pink
    TICKET_ONE_USE: { letter: '1', color: '#607D8B', textColor: 'white' }, // Blue Grey
};

const defaultTypeConfig: TypeConfig = {
    letter: 'X',
    color: '#9E9E9E',
    textColor: 'white',
};

/**
 * Display device types as chips
 */
export function SttChipDeviceType({ types = [] }: Props): React.ReactElement {
    const { t } = useTranslation();

    // Flatten the types array to extract actual type strings
    // API returns: [{type: [{type: 'CARD'}, {type: 'QR'}]}]
    // We need: ['CARD', 'QR']
    const flattenedTypes: string[] = [];

    if (Array.isArray(types)) {
        types.forEach((typeObj: any) => {
            if (typeof typeObj === 'string') {
                // Direct string: ['PIN', 'QR']
                flattenedTypes.push(typeObj);
            } else if (typeObj?.type) {
                if (typeof typeObj.type === 'string') {
                    // Simple object: [{type: 'PIN'}]
                    flattenedTypes.push(typeObj.type);
                } else if (Array.isArray(typeObj.type)) {
                    // Nested array: [{type: [{type: 'CARD'}, {type: 'QR'}]}]
                    typeObj.type.forEach((innerType: any) => {
                        if (typeof innerType === 'string') {
                            flattenedTypes.push(innerType);
                        } else if (typeof innerType?.type === 'string') {
                            flattenedTypes.push(innerType.type);
                        }
                    });
                }
            }
        });
    }

    return (
        <Box display="flex" flexDirection="row">
            {flattenedTypes.map((typeValue: string, index: number) => {
                const config =
                    typeConfig[typeValue as DeviceType] || defaultTypeConfig;

                return (
                    <Chip
                        key={`${typeValue}-${index}`}
                        size="small"
                        style={{
                            marginRight: 3,
                            marginBottom: 3,
                            ...chipColors(config.color),
                        }}
                        avatar={
                            <Avatar
                                style={{
                                    backgroundColor: 'rgba(255,255,255,0.95)',
                                    color: onLight(config.color),
                                    fontWeight: 600,
                                }}
                            >
                                {config.letter}
                            </Avatar>
                        }
                        label={t(`accessTypes.${typeValue.toLowerCase()}`)}
                    />
                );
            })}
        </Box>
    );
}
